import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { bookingService, buildWebsiteEnquiryPayload } from '../services/bookingService';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  MessageCircle, 
  Users,
  Car,
  Shield,
  CheckCircle,
  Send,
  Calendar,
  Navigation
} from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleType: '',
    pickupLocation: '',
    dropLocation: '',
    pickupDate: '',
    pickupTime: '',
    passengers: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.vehicleType) newErrors.vehicleType = 'Please select a vehicle';
    if (!formData.pickupLocation.trim()) newErrors.pickupLocation = 'Pickup location is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = buildWebsiteEnquiryPayload({
        customerName: formData.name,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        subject: `${formData.vehicleType || 'Vehicle'} booking enquiry`,
        message:
          formData.message?.trim() ||
          `Booking enquiry for ${formData.vehicleType}. Pickup: ${formData.pickupLocation}. Drop: ${formData.dropLocation || 'Not provided'}. Pickup date: ${formData.pickupDate || 'Flexible'}. Pickup time: ${formData.pickupTime || 'Flexible'}.`,
        expectedTravelDate: formData.pickupDate || undefined,
        expectedPax: formData.passengers || undefined,
        selectedVehicleTypes: [formData.vehicleType],
        pickupLocation: formData.pickupLocation,
        dropLocation: formData.dropLocation,
        tripType: formData.dropLocation ? 'ONE_WAY' : undefined,
        customFields: {
          pickupTime: formData.pickupTime || undefined,
          passengerCount: formData.passengers || undefined,
          vehiclePreference: formData.vehicleType || undefined,
          additionalPreferences: formData.message || undefined,
        },
      });

      await bookingService.submitWebsiteEnquiry(payload);

      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        vehicleType: '',
        pickupLocation: '',
        dropLocation: '',
        pickupDate: '',
        pickupTime: '',
        passengers: '',
        message: ''
      });
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      setErrors({
        submit: error.message || 'Failed to submit enquiry. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <>
      <Helmet>
        <title>Contact Plus Cabs | Book Innova, Tempo Traveller & Sedan in Bangalore - Call +91 734 951 9354</title>
        <meta 
          name="description" 
          content="Contact Plus Cabs for Innova Crysta, Innova Hycross, Tempo Traveller, Sedan & SUV booking in Bangalore. 24/7 service. Call 📞 +91 734 951 9354 for instant booking."
        />
        <meta name="keywords" content="contact plus cabs, innova crysta booking contact, tempo traveller booking number, sedan rental bangalore, suv hire contact, cab service bangalore contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Plus Cabs",
            "description": "Book Innova Crysta, Tempo Traveller & Sedan in Bangalore",
            "url": "https://pluscabs.com/contact",
            "mainEntity": {
              "@type": "Organization",
              "name": "Plus Cabs",
              "telephone": "+917349519354",
              "email": "achalatours22@gmail.com",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "41, 1st A Cross Rd, Zuzuvadi, HSR layout Sector 2, 1st Stage, BTM Layout",
                "addressLocality": "Bengaluru",
                "addressRegion": "Karnataka",
                "postalCode": "560068",
                "addressCountry": "India"
              }
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white py-8">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-3">
              Contact Plus Cabs
            </h1>
            <p className="text-lg opacity-90 max-w-2xl mx-auto">
              Get instant booking for Innova Crysta, Innova Hycross, Tempo Traveller, Sedan & SUVs in Bangalore
            </p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              {/* Quick Contact Cards */}
              <div className="bg-white rounded-lg shadow p-5">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Phone className="w-5 h-5 mr-2 text-yellow-600" />
                  Instant Booking
                </h2>
                
                <div className="space-y-3">
                  <a 
                    href="tel:+917349519354" 
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="bg-green-100 p-2 rounded-lg mr-3">
                        <Phone className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">+91 734 951 9354</div>
                        <div className="text-sm text-gray-600">Primary Number</div>
                      </div>
                    </div>
                  </a>

                  <a 
                    href="https://wa.me/917349519354" 
                    className="flex items-center justify-between p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="flex items-center">
                      <MessageCircle className="w-5 h-5 mr-3" />
                      <div>
                        <div className="font-bold">WhatsApp Booking</div>
                        <div className="text-sm opacity-90">Instant response</div>
                      </div>
                    </div>
                    <div className="text-xs bg-white/20 px-2 py-1 rounded">Recommended</div>
                  </a>
                </div>
              </div>

              {/* Business Info */}
              <div className="bg-white rounded-lg shadow p-5">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Business Information</h2>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-yellow-600 mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-900">Office Address</div>
                      <div className="text-sm text-gray-600">
                        41, 1st A Cross Rd, Zuzuvadi,<br />
                        HSR layout Sector 2, 1st Stage,<br />
                        BTM Layout, Bengaluru,<br />
                        Karnataka 560068
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-yellow-600 mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-900">Service Areas</div>
                      <div className="text-sm text-gray-600">
                        All Bangalore areas including Whitefield, Electronic City, Koramangala, 
                        Indiranagar, HSR Layout, JP Nagar, BTM Layout, Airport Zone, and surrounding areas
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Clock className="w-5 h-5 text-yellow-600 mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-900">Operating Hours</div>
                      <div className="text-sm text-gray-600">24/7 Service Available</div>
                      <div className="text-xs text-gray-500">365 days a year</div>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Mail className="w-5 h-5 text-yellow-600 mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-900">Email Address</div>
                      <a 
                        href="mailto:achalatours22@gmail.com" 
                        className="text-sm text-yellow-600 hover:text-yellow-800 break-all"
                      >
                        achalatours22@gmail.com
                      </a>
                      <div className="text-xs text-gray-500">Response within 2 hours</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Why Choose Us */}
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg shadow p-5">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Why Choose Plus Cabs</h2>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Shield className="w-4 h-4 text-green-600 mr-2" />
                    <span className="text-sm text-gray-700">Fully Insured Vehicles</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="w-4 h-4 text-blue-600 mr-2" />
                    <span className="text-sm text-gray-700">Experienced Professional Drivers</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                    <span className="text-sm text-gray-700">24/7 Customer Support</span>
                  </div>
                  <div className="flex items-center">
                    <Car className="w-4 h-4 text-blue-600 mr-2" />
                    <span className="text-sm text-gray-700">Well-maintained Fleet</span>
                  </div>
                  <div className="flex items-center">
                    <Navigation className="w-4 h-4 text-blue-600 mr-2" />
                    <span className="text-sm text-gray-700">GPS Equipped Vehicles</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow p-5 md:p-6">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Booking Request Submitted!</h3>
                    <p className="text-gray-600 mb-4">
                      We have received your booking request. Our team will contact you within 15 minutes.
                    </p>
                    <div className="bg-yellow-50 rounded-lg p-4 max-w-md mx-auto">
                      <p className="text-sm text-gray-700 mb-2">
                        For immediate assistance, call us directly:
                      </p>
                      <a 
                        href="tel:+917349519354" 
                        className="inline-flex items-center text-yellow-600 font-bold"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        +91 734 951 9354
                      </a>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Quick Booking Request
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Fill the form below and our team will contact you within 15 minutes
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      {errors.submit && (
                        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                          {errors.submit}
                        </div>
                      )}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 rounded-lg border ${
                              errors.name ? 'border-red-500' : 'border-gray-300'
                            } focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none`}
                            placeholder="Enter your full name"
                          />
                          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number *
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 rounded-lg border ${
                              errors.phone ? 'border-red-500' : 'border-gray-300'
                            } focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none`}
                            placeholder="10-digit mobile number"
                          />
                          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 rounded-lg border ${
                              errors.email ? 'border-red-500' : 'border-gray-300'
                            } focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none`}
                            placeholder="your@email.com"
                          />
                          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Vehicle Type *
                          </label>
                          <select
                            name="vehicleType"
                            value={formData.vehicleType}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 rounded-lg border ${
                              errors.vehicleType ? 'border-red-500' : 'border-gray-300'
                            } focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none`}
                          >
                            <option value="">Select Vehicle</option>
                            <option value="innova-crysta">Toyota Innova Crysta</option>
                            <option value="innova-hycross">Toyota Innova Hycross</option>
                            <option value="innova-base">Toyota Innova (Base)</option>
                            <option value="suv">SUV (Hyundai Creta, Kia Seltos, XUV700)</option>
                            <option value="sedan">Sedan (Swift Dzire, Honda City)</option>
                            <option value="hatchback">Hatchback (WagonR, i10)</option>
                            <option value="ertiga">Maruti Suzuki Ertiga</option>
                            <option value="carens">Kia Carens</option>
                            <option value="tempo-9">Tempo Traveller 9 Seater</option>
                            <option value="tempo-12">Tempo Traveller 12 Seater</option>
                            <option value="tempo-15">Tempo Traveller 15 Seater</option>
                            <option value="tempo-18">Tempo Traveller 18 Seater</option>
                            <option value="mini-bus-20">Mini Bus 20 Seater</option>
                            <option value="mini-bus-26">Mini Bus 26 Seater</option>
                            <option value="bus-32">Bus 32 Seater</option>
                            <option value="bus-45">Bus 45 Seater</option>
                          </select>
                          {errors.vehicleType && <p className="text-red-500 text-xs mt-1">{errors.vehicleType}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Pickup Location *
                          </label>
                          <input
                            type="text"
                            name="pickupLocation"
                            value={formData.pickupLocation}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 rounded-lg border ${
                              errors.pickupLocation ? 'border-red-500' : 'border-gray-300'
                            } focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none`}
                            placeholder="Enter pickup location"
                          />
                          {errors.pickupLocation && <p className="text-red-500 text-xs mt-1">{errors.pickupLocation}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Drop Location
                          </label>
                          <input
                            type="text"
                            name="dropLocation"
                            value={formData.dropLocation}
                            onChange={handleChange}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none"
                            placeholder="Enter drop location"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Pickup Date
                          </label>
                          <div className="relative">
                            <Calendar className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
                            <input
                              type="date"
                              name="pickupDate"
                              value={formData.pickupDate}
                              onChange={handleChange}
                              min={today}
                              className="w-full pl-10 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Pickup Time
                          </label>
                          <input
                            type="time"
                            name="pickupTime"
                            value={formData.pickupTime}
                            onChange={handleChange}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Number of Passengers
                          </label>
                          <input
                            type="number"
                            name="passengers"
                            value={formData.passengers}
                            onChange={handleChange}
                            min="1"
                            max="50"
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none"
                            placeholder="Number of passengers"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Additional Message (Optional)
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows="3"
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none resize-none"
                          placeholder="Any special requirements or details..."
                        ></textarea>
                      </div>

                      <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                        <div className="flex items-center">
                          <Phone className="w-5 h-5 text-yellow-600 mr-3" />
                          <div>
                            <p className="text-sm text-gray-700">
                              For urgent bookings or immediate assistance, call us directly:
                            </p>
                            <a 
                              href="tel:+917349519354" 
                              className="text-yellow-600 font-bold hover:text-yellow-800"
                            >
                              +91 734 951 9354
                            </a>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-yellow-600 hover:to-yellow-700 transition-all shadow hover:shadow-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                              Processing...
                            </>
                          ) : (
                            <>
                              <Send className="w-5 h-5 mr-2" />
                              Submit Booking Request
                            </>
                          )}
                        </button>
                        
                        <a 
                          href="https://wa.me/917349519354" 
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-all shadow hover:shadow-lg flex items-center justify-center"
                        >
                          <MessageCircle className="w-5 h-5 mr-2" />
                          WhatsApp Instant Booking
                        </a>
                      </div>

                      <p className="text-xs text-gray-500 text-center">
                        By submitting this form, you agree to our terms and conditions. We respect your privacy.
                      </p>
                    </form>
                  </>
                )}
              </div>

              {/* Vehicle Categories */}
              <div className="mt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Popular Vehicle Categories</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <a 
                    href="/innova-crysta-booking-bangalore"
                    className="bg-white rounded-lg shadow p-3 hover:shadow-md transition-shadow text-center"
                  >
                    <div className="font-semibold text-gray-900 text-sm">Innova Crysta</div>
                    <div className="text-xs text-gray-600">7 Seater Premium</div>
                  </a>
                  <a 
                    href="/innova-hycross-booking"
                    className="bg-white rounded-lg shadow p-3 hover:shadow-md transition-shadow text-center"
                  >
                    <div className="font-semibold text-gray-900 text-sm">Innova Hycross</div>
                    <div className="text-xs text-gray-600">Hybrid 7 Seater</div>
                  </a>
                  <a 
                    href="/tempo-traveller-booking"
                    className="bg-white rounded-lg shadow p-3 hover:shadow-md transition-shadow text-center"
                  >
                    <div className="font-semibold text-gray-900 text-sm">Tempo Traveller</div>
                    <div className="text-xs text-gray-600">9-18 Seater AC</div>
                  </a>
                  <a 
                    href="/sedan-booking-bangalore"
                    className="bg-white rounded-lg shadow p-3 hover:shadow-md transition-shadow text-center"
                  >
                    <div className="font-semibold text-gray-900 text-sm">Sedan</div>
                    <div className="text-xs text-gray-600">4 Seater Comfort</div>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Frequently Asked Questions
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="font-bold text-gray-900 mb-3">How quickly can I get a vehicle?</h3>
                <p className="text-gray-600 text-sm">
                  For immediate requirements, we can provide vehicles within 30-60 minutes in Bangalore city. 
                  For advance bookings, please book at least 2 hours in advance.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="font-bold text-gray-900 mb-3">What payment methods do you accept?</h3>
                <p className="text-gray-600 text-sm">
                  We accept cash, UPI, credit/debit cards, net banking, and corporate billing. 
                  For corporate clients, we provide monthly billing with GST invoices.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="font-bold text-gray-900 mb-3">Do you provide drivers?</h3>
                <p className="text-gray-600 text-sm">
                  Yes, all our vehicles come with experienced, professional drivers who are 
                  well-trained and familiar with Bangalore routes and traffic patterns.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="font-bold text-gray-900 mb-3">What is your cancellation policy?</h3>
                <p className="text-gray-600 text-sm">
                  Free cancellation up to 2 hours before pickup time. For cancellations within 
                  2 hours, a nominal cancellation charge may apply depending on vehicle type.
                </p>
              </div>
            </div>
          </div>

          {/* Service Areas */}
          <div className="mt-12 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
              Service Areas in Bangalore
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="font-semibold text-gray-900">East Bangalore</div>
                <div className="text-xs text-gray-600 mt-1">Whitefield, Marathahalli, KR Puram, Indiranagar</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="font-semibold text-gray-900">South Bangalore</div>
                <div className="text-xs text-gray-600 mt-1">JP Nagar, Banashankari, HSR, Koramangala, BTM Layout</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="font-semibold text-gray-900">North Bangalore</div>
                <div className="text-xs text-gray-600 mt-1">Hebbal, Yelahanka, Malleshwaram, Rajajinagar</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="font-semibold text-gray-900">Central & West</div>
                <div className="text-xs text-gray-600 mt-1">MG Road, Brigade Road, Yeshwanthpur, Peenya</div>
              </div>
            </div>
            
            <div className="text-center mt-6">
              <p className="text-gray-700">
                We service <strong>all areas of Bangalore</strong> including Kempegowda International Airport (BLR)
              </p>
            </div>

            <div className="mt-8">
              <div className="bg-white/80 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-yellow-100">
                <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Find Our Location in Bangalore</h2>
                <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
                  Visit our office at HSR Layout, Bangalore for car rental bookings and inquiries. 
                  We're conveniently located with easy access to major routes.
                </p>
                
                <div className="rounded-2xl overflow-hidden shadow-2xl border-2 border-yellow-100">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d3888.7294896112026!2d77.6166!3d12.9251!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1457b2634b49%3A0x4add9529175916f3!2s41%2C%201st%20A%20Cross%20Rd%2C%20Zuzuvadi%2C%20HSR%20layout%20Sector%202%2C%201st%20Stage%2C%20BTM%20Layout%2C%20Bengaluru%2C%20Karnataka%20560068!5e0!3m2!1sen!2sin!4v1771525553804!5m2!1sen!2sin"
                    width="100%"
                    height="450"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Plus Cabs Office Location - HSR Layout, Bangalore"
                    className="w-full"
                  />
                </div>
                
                <div className="mt-6 text-center">
                  <p className="text-gray-600 mb-4">
                    <strong>Address:</strong> 41, 1st A Cross Rd, Zuzuvadi, HSR layout Sector 2, 1st Stage, BTM Layout, Bengaluru, Karnataka 560068
                  </p>
                  <a
                    href="https://maps.app.goo.gl/LgCx1a4uGQNuZJmm6"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-semibold rounded-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                  >
                    <MapPin className="w-5 h-5 mr-2" />
                    Get Directions on Google Maps
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
